import {
  ApiError,
  type MachineImportResource,
  MachineImportsController_file_v1,
  MachineImportsController_findOne_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { IconAlertTriangle, IconArrowLeft, IconDownload, IconFileOff } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isNotFound, messageForFailure } from '@/api/failure';
import { formatInstant } from '@/lib/instant';

import { LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { machineBillsForImportPath, paths } from '../paths';
import { saveDownloadedFile } from '../reporting/cash-book-export';
import { Bills, Days, Tally } from './ImportOutcome';

/**
 * One upload as it was answered: `GET /v1/machine-imports/{id}`, and the
 * original file (`…/file`) — the exact bytes the machine wrote, fetched on the
 * authenticated transport and saved, as the CSV exports are.
 */

type RecordState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: MachineImportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

export function MachineImport() {
  const { id } = useParams();
  const client = useApiClient();

  const [state, setState] = useState<RecordState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>(null);

  useEffect(() => {
    if (id === undefined) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(MachineImportsController_findOne_v1, { path: { id }, signal: controller.signal })
      .then((value) => {
        if (live) setState({ kind: 'loaded', value });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  if (id === undefined) return <Navigate to={paths.machineUploads} replace />;

  const upload = state.kind === 'loaded' ? state.value : null;

  const missing =
    state.kind === 'failed' &&
    (isNotFound(state.error) ||
      (state.error instanceof ApiError && state.error.code === 'VALIDATION_FAILED'));

  const download = () => {
    if (upload === null || downloading) return;

    setDownloading(true);
    setDownloadError(null);

    client
      .download(MachineImportsController_file_v1, { path: { id } })
      .then((file) => {
        saveDownloadedFile(file, upload.fileName);
      })
      .catch((error: unknown) => {
        setDownloadError(error);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <Screen
      title="Machine upload"
      description="One billing machine file as its upload answered: every bill's outcome and the days it added to. The original file is kept byte for byte and can be downloaded."
      action={
        upload !== null && (
          <Button variant="outline" onClick={download} disabled={downloading}>
            <IconDownload data-icon="inline-start" />
            Download the original
          </Button>
        )
      }
      aside={
        <>
          {upload !== null && (
            <>
              <ScreenFact label="File">
                <span className="font-mono break-all">{upload.fileName}</span>
              </ScreenFact>

              <ScreenFact label="Uploaded">
                {upload.recordedAt === null ? '' : formatInstant(upload.recordedAt)}
                {upload.uploadedBy !== null && (
                  <span className="block text-sm text-muted-foreground">
                    by {upload.uploadedBy.displayName}
                  </span>
                )}
              </ScreenFact>

              <ScreenFact label="In the file">
                <span className="font-mono numeric">{upload.billCount}</span>{' '}
                {upload.billCount === 1 ? 'bill' : 'bills'} on{' '}
                <span className="font-mono numeric">{upload.rowCount}</span>{' '}
                {upload.rowCount === 1 ? 'row' : 'rows'}
              </ScreenFact>

              <Link
                to={machineBillsForImportPath(id)}
                className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
              >
                The bills it created
              </Link>
            </>
          )}

          <Link
            to={paths.machineUploads}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the uploads
          </Link>
        </>
      }
    >
      {missing ? (
        <Alert>
          <IconFileOff />
          <AlertTitle>There is no machine upload at this address</AlertTitle>
          <AlertDescription>This link may be out of date or mistyped.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-8 pt-2">
          {state.kind === 'loading' && (
            <div aria-hidden className="h-64 animate-pulse rounded-3xl bg-muted" />
          )}

          {state.kind === 'failed' && (
            <div className="mx-auto max-w-md space-y-3 py-8 text-center">
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
              />
            </div>
          )}

          {downloadError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{messageForFailure(downloadError)}</AlertDescription>
            </Alert>
          )}

          {upload !== null && (
            <>
              <Tally counts={upload.counts} preview={false} />
              <Days days={upload.days} preview={false} />
              <Bills bills={upload.bills} preview={false} />
              <p className="text-xs break-all text-muted-foreground">
                SHA-256 <span className="font-mono">{upload.fileSha256}</span>
              </p>
            </>
          )}
        </div>
      )}
    </Screen>
  );
}
